import { CommonModule, Location } from '@angular/common';
import { Component, DestroyRef, Inject, inject, LOCALE_ID, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterModule, UrlTree } from '@angular/router';
import { catchError, finalize, map, of, take, tap } from 'rxjs';
import { fadeAnimation } from 'shared/src/animations/fade-animation';
import { LocalStorageKey, LocalStorageService } from 'shared/src/services/local-storage.service';
import { UserInfoService } from 'shared/src/services/user-info.service';
import { PROJECT_NAME, ProjectName, ProjectNames } from 'shared/src/tokens';
import { AnalyticsService } from '../../services/analytics.service';
import { Addon, ApiService, Placement } from '../../services/api.service';
import { AxonService } from '../../services/axon.service';
import { NewsbreakService } from '../../services/newsbreak.service';
import { formatCurrency } from '../../utils/currency.util';
import { AppDownloadComponent } from '../app-download/app-download.component';
import { UpsellComponent, UpsellOffer } from '../upsell/upsell.component';

interface AddonImageConfig {
  class: string; // aspect-ratio class in upsell.component.scss
  name: string; // base file name under shared/assets/upsells/
  themed: boolean; // has _white / _dark variants
  localized: boolean; // lives in a per-locale subdir (unknown locales fall back to en/)
}

interface AddonUiConfig {
  title: string;
  subtitle?: string;
  description: string;
  cta: string;
  image?: AddonImageConfig;
  colorScheme: 'green' | 'orange' | 'blue';
}

// Locales that ship localized upsell artwork; anything else falls back to en.
const UPSELL_IMAGE_LOCALES = ['en', 'de', 'es', 'fr', 'it', 'pl', 'pt', 'tr'];

function span(className: string, text: string): string {
  return `<span class="${className}">${text}</span>`;
}

function italic(text: string): string {
  return `<i>${text}</i>`;
}

const ADDON_METADATA: Record<string, AddonUiConfig> = {
  'productivity-summaries': {
    title: `${$localize`:@@addonProductivitySummariesTitlePart1:25 Life-Changing Books.`} ${span(
      'main-color',
      $localize`:@@addonProductivitySummariesTitlePart2:15-Minute Summaries.`
    )}`,
    subtitle: `${$localize`:@@addonProductivitySummariesSubtitleIntro:Key ideas from bestsellers like`} ${italic(
      $localize`:@@book5AmClub:5 AM Club`
    )} ${$localize`:@@and:and`} ${italic($localize`:@@bookCantHurtMe:Can't Hurt Me`)} ${$localize`:@@addonProductivitySummariesSubtitleOutro:- ready in minutes, not hours.`}`,
    image: { class: 'img-summaries-1', name: 'summaries_upsell1', themed: false, localized: false },
    description: $localize`:@@addonProductivitySummariesDescription:Skip hundreds of pages. Get the powerful ideas instantly in your Peech Library.`,
    cta: $localize`:@@addonProductivitySummariesCta:Add Summaries`,
    colorScheme: 'green',
  },
  'leadership-summaries': {
    title: `${$localize`:@@addonLeadershipSummariesTitlePart1:30 Brilliant Minds.`} ${span(
      'orange-color',
      $localize`:@@addonLeadershipSummariesTitlePart2:Big Ideas in 15 Minutes.`
    )}`,
    subtitle: $localize`:@@addonLeadershipSummariesSubtitle:You're building an amazing collection! Add leadership insights and biographies of visionaries.`,
    image: { class: 'img-summaries-2', name: 'summaries_upsell2', themed: false, localized: false },
    description: $localize`:@@addonLeadershipSummariesDescription:Includes summaries from Steve Jobs, Warren Buffett, Richard Branson, and more. These aren't full books - they're expertly distilled lessons and strategies, packed into quick 15-minute reads.`,
    cta: $localize`:@@addonLeadershipSummariesCta:Complete My Library`,
    colorScheme: 'orange',
  },
  'leadership-productivity-summaries': {
    title: `${span(
      'orange-color',
      $localize`:@@addonLeadershipProductivitySummariesTitlePart1:Our Most Generous Offer:`
    )} ${$localize`:@@addonLeadershipProductivitySummariesTitlePart2:55 Game-Changing Books`}`,
    subtitle: $localize`:@@addonLeadershipProductivitySummariesSubtitle:Everything from productivity AND leadership collections — the complete bundle at our lowest price ever.`,
    image: { class: 'img-summaries-3', name: 'summaries_upsell3', themed: false, localized: false },
    description: `${$localize`:@@addonLeadershipProductivitySummariesDescriptionIntro:Get both collections for less than half the price of one! Expert summaries of productivity classics like`} ${italic(
      $localize`:@@bookAtomicHabits:Atomic Habits`
    )} ${$localize`:@@addonLeadershipProductivitySummariesDescriptionOutro:PLUS leadership insights from Steve Jobs, Warren Buffett, and 53 other brilliant minds - packed quick into 15-minute reads.`}`,
    cta: $localize`:@@addonLeadershipProductivitySummariesCta:Claim This Deal`,
    colorScheme: 'orange',
  },
  'playback-speed': {
    title: `${$localize`:@@addonPlaybackSpeedTitlePart1:Listen up to`} ${span(
      'main-color',
      $localize`:@@addonPlaybackSpeedTitlePart2:Three Times`
    )} ${$localize`:@@addonPlaybackSpeedTitlePart3:faster`}`,
    subtitle: $localize`:@@addonPlaybackSpeedSubtitle:Users save an average of 5+ hours per week listening at higher speeds`,
    description: $localize`:@@addonPlaybackSpeedDescription:Speed through any audio and save hours every week. Make your learning and work faster, easier and more productive.`,
    image: { class: 'img-speed', name: 'speed_upsell', themed: true, localized: false },
    cta: $localize`:@@addonPlaybackSpeedCta:Add Speed Options`,
    colorScheme: 'green',
  },
  // used on main funnel as 3rd option after summaries + on feature related funnels
  'voice-presets-2': {
    title: `${$localize`:@@addonVoicePresets2TitlePart1:Two`} ${span(
      'blue-color',
      $localize`:@@addonVoicePresets2TitlePart2:exclusive premium`
    )} ${$localize`:@@addonVoicePresets2TitlePart3:voices`}`,
    description: `${$localize`:@@addonVoicePresets2DescriptionPart1:Give every audio a richer, more natural and more expressive sound. Created together with our partner`} ${span(
      'fw-bold',
      $localize`:@@openAiBrand:OpenAI`
    )} ${$localize`:@@addonVoicePresets2DescriptionPart2:and available only here.`}`,
    image: { class: 'img-voice-presets', name: 'voices_2_upsell', themed: true, localized: true },
    cta: $localize`:@@addonVoicePresets2Cta:Add Voice Presets`,
    colorScheme: 'blue',
  },
  //used in case none was purchased:
  'voice-presets-2-playback-speed': {
    title: `${$localize`:@@addonVoiceSpeedBundleTitlePart1:Ultimate Bundle:`} ${span(
      'blue-color',
      $localize`:@@addonVoiceSpeedBundleTitlePart2:Richer Sound`
    )} ${$localize`:@@ampersand:&`} ${span(
      'orange-color',
      $localize`:@@addonVoiceSpeedBundleTitlePart3:Faster Listening`
    )}`,
    description: $localize`:@@addonVoiceSpeedBundleDescription:Get both premium OpenAI voices AND playback speed up to 3x - at our lowest price ever.`,
    image: {
      class: 'img-voice-speed-bundle',
      name: 'speed_voices_2_upsell',
      themed: true,
      localized: true,
    },
    cta: $localize`:@@addonVoiceSpeedBundleCta:Claim This Deal`,
    colorScheme: 'orange',
  },
};

interface ProgressStep {
  title: string;
  completed: boolean;
  current: boolean;
}

@Component({
  selector: 'app-post-payment-flow',
  imports: [
    CommonModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    RouterModule,
    UpsellComponent,
    AppDownloadComponent,
  ],
  templateUrl: './post-payment-flow.component.html',
  styleUrl: './post-payment-flow.component.scss',
  animations: [fadeAnimation],
})
export class PostPaymentFlowComponent implements OnInit {
  currentStepIndex = 0;
  isSuccess = true;
  checkoutUrl = '/paywall'; // default fallback

  /**
   * Where to continue to instead of dead-ending on the app-store step. Only the web app sets
   * it (via the `redirect` param the checkout threads through), so funnels keep their
   * existing terminal step untouched. Parsed rather than kept as a string: the return path
   * carries the library's own query string (e.g. '/?tab=inProgress'), and routerLink treats
   * a raw string as a single path segment, escaping the '?' into a 404.
   */
  continueUrl: UrlTree | undefined;

  /**
   * Destination of the failure step's "Try again" link. Carries the return path forward so a
   * successful retry still lands somewhere that can send the user back into the app.
   */
  retryUrl: UrlTree | string = this.checkoutUrl;

  // Progress steps - will be initialized based on whether upsells exist
  progressSteps: ProgressStep[] = [];

  isLoadingUpsell = false;

  // Upsell offers - configured dynamically from API
  private upsellPlacementSequence: Placement[] = [];
  private upsellOffers: (UpsellOffer | null)[] = [];

  // Track purchased upsells to determine fallback offers - keyed by base placement, so the
  // feature upsells' _x1.5 price suffix never hides an addon the user already bought.
  private purchasedUpsells: Set<string> = new Set();

  private shouldShowFeatureUpsells = false; // for traffic with listening intent we show features, for the rest - content

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private analyticsService: AnalyticsService,
    private userInfo: UserInfoService,
    private localStorageService: LocalStorageService,
    private apiService: ApiService,
    private axonService: AxonService,
    private newsbreakService: NewsbreakService,
    @Inject(PROJECT_NAME) private projectName: ProjectName,
    @Inject(LOCALE_ID) private locale: string
  ) {}

  ngOnInit(): void {
    // Get checkoutUrl from route data if provided
    const routeCheckoutUrl = this.route.snapshot.data['checkoutUrl'];

    if (routeCheckoutUrl) {
      this.checkoutUrl = routeCheckoutUrl;
    }

    // This page can load in a context that never saw the checkout (a Stripe redirect landing
    // in another browser, a re-opened result link) — localStorage is empty there, and the URL
    // params written by PaymentHandlerService are the only identity available.
    this.userInfo.hydrateFromUrl();

    const redirectStatus = this.route.snapshot.queryParamMap.get('redirect_status');

    this.isSuccess = redirectStatus !== 'failed';

    // A protocol-relative '//host' is an off-site redirect in disguise, so only a single
    // leading slash passes — this value arrives in a URL and is not to be trusted.
    const redirect = this.route.snapshot.queryParamMap.get('redirect');
    const safeRedirect =
      redirect && redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : undefined;

    this.continueUrl = safeRedirect ? this.router.parseUrl(safeRedirect) : undefined;
    this.retryUrl = safeRedirect
      ? this.router.createUrlTree([this.checkoutUrl], { queryParams: { redirect: safeRedirect } })
      : this.checkoutUrl;

    this.shouldShowFeatureUpsells = (
      [
        ProjectNames.GOOGLE_FUNNEL,
        ProjectNames.SHORT_FUNNEL,
        ProjectNames.KINDLE_FUNNEL,
        ProjectNames.ROMANCE_FUNNEL,
      ] as ProjectName[]
    ).includes(this.projectName);

    const email = this.userInfo.getEmail();
    const stripeCustomerId = this.userInfo.getStripeCustomerId();

    const product_code = this.route.snapshot.queryParamMap.get('product_code');

    // Only skip summary-related upsells if product contains addon-summaries
    const hasSummariesInProduct = !!(product_code && product_code.includes('addon-summaries'));

    const skipUpsells = this.route.snapshot.queryParamMap.get('skip_upsells') === 'true';
    const shouldShowUpsells = this.isSuccess && !skipUpsells && (!!email || !!stripeCustomerId);

    // Determine upsell sequence based on project type
    if (shouldShowUpsells) {
      this.upsellPlacementSequence = this.getUpsellSequenceForProject(hasSummariesInProduct);
    } else {
      this.upsellPlacementSequence = [];
    }

    this.upsellOffers = new Array(this.upsellPlacementSequence.length).fill(null);

    // Initialize progress steps based on whether upsells exist
    this.initializeProgressSteps();

    // Determine starting step - always start at 0 (upsell if available, otherwise final)
    this.currentStepIndex =
      this.upsellPlacementSequence.length > 0 ? 0 : this.upsellPlacementSequence.length;
    this.updateProgressSteps();

    if (this.isUpsellStep()) {
      this.loadCurrentUpsellOffer();
    }

    // Purchase/failure events fire only on the arrival that actually completed the checkout:
    // either Stripe's redirect (it appends redirect_status to the return URL) or an in-app
    // navigation carrying the fromCheckout marker — the paywall's Apple Pay / Google Pay
    // sheet lands here that way, via navigateToSuccess, which sets the marker. Any other load
    // of this URL — tab restore, history, a link re-opened in another browser — is a revisit
    // of an already-tracked result, and re-firing here is what used to send the BI phantom
    // Purchases with no email.
    const cameFromCheckout = redirectStatus !== null || history.state?.['fromCheckout'] === true;

    if (!cameFromCheckout) {
      return;
    }

    if (skipUpsells) {
      // Reached here from the duplicate-subscription error flow: the user already has an
      // active subscription, so this is NOT a new purchase. Track it as a failure (not a
      // success/purchase) — this component owns result-page tracking.
      this.analyticsService.track('web_subscribe_failure', {
        error: 'DUPLICATE_SUBSCRIPTION',
        product_code,
      });
      this.consumeCheckoutArrivalMarkers();
    } else if (this.isSuccess) {
      this.analyticsService.track('web_subscribe_success', {
        product_code,
        email,
      });

      // Track purchase for pixels
      this.trackSubscriptionPurchase(product_code);

      this.consumeCheckoutArrivalMarkers();
    } else {
      // Failed redirects keep their markers: redirect_status=failed also drives the failure
      // UI, so stripping it would flip a refresh to the success screen.
      this.analyticsService.track('web_subscribe_failure', { product_code });
    }
  }

  /**
   * Without this, F5 double-tracks the purchase: both arrival markers survive a refresh —
   * redirect_status because it is in the URL, fromCheckout because history.state belongs to
   * the history entry, not the page. Rewriting the history entry without them makes a refresh
   * indistinguishable from any other revisit, which the cameFromCheckout gate already
   * ignores. Location.replaceState (vs a router navigation) leaves the component — and the
   * upsell flow it already started — untouched.
   */
  private consumeCheckoutArrivalMarkers(): void {
    const queryParams = { ...this.route.snapshot.queryParams };

    // Stripe appends these to the return URL alongside redirect_status.
    for (const param of [
      'redirect_status',
      'payment_intent',
      'payment_intent_client_secret',
      'setup_intent',
      'setup_intent_client_secret',
      'source_type',
    ]) {
      delete queryParams[param];
    }

    const url = this.router.createUrlTree([], { relativeTo: this.route, queryParams });

    this.location.replaceState(this.router.serializeUrl(url));
  }

  /**
   * Returns the upsell placement sequence based on the project type.
   *
   * - Feature funnels (google / short / kindle / romance), at the x1.5 prices that won the
   *   Aug 2026 price test:
   *   1. upsell_voice_presets_x1.5
   *   2. upsell_speed_x1.5
   *   3. upsell_voice_presets_speed_x1.5 (if user didn't buy 1 or 2)
   *
   * - Regular Funnel:
   *   1. upsell_1 (skip if product has summaries)
   *   2. upsell_2 or upsell_2_discount (skip if product has summaries)
   *   3. upsell_voice_presets_discount
   */
  private getUpsellSequenceForProject(hasSummariesInProduct: boolean): Placement[] {
    if (this.shouldShowFeatureUpsells) {
      // voice presets -> speed -> combined bundle
      return ['upsell_voice_presets_x1.5', 'upsell_speed_x1.5', 'upsell_voice_presets_speed_x1.5'];
    } else {
      if (hasSummariesInProduct) {
        // Skip summary upsells, only show voice presets discount
        return ['upsell_voice_presets_discount'];
      }

      return [
        'upsell_productivity_summaries',
        'upsell_leadership_summaries',
        'upsell_voice_presets_discount',
      ];
    }
  }

  private initializeProgressSteps(): void {
    this.progressSteps = [
      {
        title: $localize`:@@postPaymentStepCompleteSetup:Complete setup`,
        completed: true,
        current: false,
      },
    ];

    // Only add the upsell step if there are upsells
    if (this.upsellPlacementSequence.length > 0) {
      this.progressSteps.push({
        title: $localize`:@@postPaymentStepAddOffer:Add offer`,
        completed: false,
        current: false,
      });
    }

    // Always add the final step
    this.progressSteps.push({
      title: this.continueUrl
        ? $localize`:@@postPaymentStepStartListening:Start listening`
        : $localize`:@@postPaymentStepGetTheApp:Get the app`,
      completed: false,
      current: false,
    });
  }

  onUpsellAction(action: 'continue' | 'skip' | 'error', upsell: UpsellOffer): void {
    if (action === 'continue') {
      this.purchasedUpsells.add(this.getPlacementBase(upsell.placement));
      this.analyticsService.track('web_upsell_accepted', {
        product_id: upsell.addonCode,
        placement: upsell.placement,
        upsell_index: this.currentStepIndex,
      });
    } else if (action === 'error') {
      this.handleUpsellSkipOrError();
      this.analyticsService.track('web_upsell_error', {
        product_id: upsell.addonCode,
        placement: upsell.placement,
      });
    } else {
      this.handleUpsellSkipOrError();
      this.analyticsService.track('web_upsell_skipped', {
        product_id: upsell.addonCode,
        placement: upsell.placement,
      });
    }

    this.nextStep();
  }

  /**
   * Handle skip/error logic to customize subsequent upsells
   */
  private handleUpsellSkipOrError(): void {
    if (this.shouldShowFeatureUpsells) {
      // For Google/Short funnel: if user is on step 1 (voice_presets after speed was skipped)
      // or if both speed and voice_presets were skipped, show the combined bundle
      // The 3rd upsell (combined) should only show if user didn't buy 1 or 2
      // This is handled by checking purchasedUpsells when loading the 3rd offer
    } else {
      this.updateRegularFlowUpsellPlacements();
    }
  }

  private nextStep(): void {
    this.currentStepIndex++;
    this.updateProgressSteps();
    this.loadCurrentUpsellOffer();
  }

  private updateProgressSteps(): void {
    // Reset all steps
    this.progressSteps.forEach(step => {
      step.current = false;
      step.completed = false;
    });

    // Step 0 (Complete setup): Always completed
    this.progressSteps[0].completed = true;

    if (this.upsellPlacementSequence.length > 0) {
      // We have upsells, so we have 3 steps: Complete setup, Add offer, Get the app
      const upsellStepIndex = 1;
      const finalStepIndex = 2;

      const isInUpsellPhase = this.currentStepIndex < this.upsellPlacementSequence.length;

      // Upsell step
      this.progressSteps[upsellStepIndex].completed = !isInUpsellPhase;
      this.progressSteps[upsellStepIndex].current = isInUpsellPhase;

      // Final step
      this.progressSteps[finalStepIndex].current =
        this.currentStepIndex >= this.upsellPlacementSequence.length;
      this.progressSteps[finalStepIndex].completed =
        this.currentStepIndex > this.upsellPlacementSequence.length;
    } else {
      // No upsells, so we have 2 steps: Complete setup, Get the app
      const finalStepIndex = 1;

      // Final step is current since there are no upsells
      this.progressSteps[finalStepIndex].current = true;
      this.progressSteps[finalStepIndex].completed = false;
    }
  }

  getCurrentUpsellOffer(): UpsellOffer | null {
    if (!this.isUpsellStep()) {
      return null;
    }

    return this.upsellOffers[this.currentStepIndex] || null;
  }

  isUpsellStep(): boolean {
    return this.currentStepIndex < this.upsellPlacementSequence.length;
  }

  isFinalStep(): boolean {
    return this.currentStepIndex >= this.upsellPlacementSequence.length;
  }

  private loadCurrentUpsellOffer(): void {
    if (!this.isUpsellStep()) {
      return;
    }

    const currentIndex = this.currentStepIndex;

    this.updateRegularFlowUpsellPlacements();

    if (this.isLoadingUpsell || this.upsellOffers[currentIndex]) {
      return;
    }

    const placement = this.upsellPlacementSequence[currentIndex];

    if (!placement) {
      return;
    }

    // Skip combined bundle (upsell_voice_presets_speed) if user purchased speed OR voice_presets
    if (this.getPlacementBase(placement) === 'upsell_voice_presets_speed') {
      const purchasedSpeed = this.purchasedUpsells.has('upsell_speed');
      const purchasedVoicePresets = this.purchasedUpsells.has('upsell_voice_presets');

      if (purchasedSpeed || purchasedVoicePresets) {
        // Skip this upsell since user already bought one of the components
        this.nextStep();
        return;
      }
    }

    let shouldAdvance = false;

    this.isLoadingUpsell = true;

    this.apiService
      .getAddons(placement)
      .pipe(
        take(1),
        map(response => this.selectAddonOffer(response.addons, placement)),
        tap(offer => {
          if (offer) {
            this.upsellOffers[currentIndex] = offer;
          } else {
            shouldAdvance = true;
          }
        }),
        catchError(error => {
          console.error(`Failed to load addons for placement ${placement}:`, error);
          shouldAdvance = true;
          return of(null);
        }),
        finalize(() => {
          this.isLoadingUpsell = false;

          if (shouldAdvance) {
            this.nextStep();
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private updateRegularFlowUpsellPlacements(): void {
    if (this.shouldShowFeatureUpsells) {
      return;
    }

    if (this.upsellPlacementSequence.length >= 2 && this.currentStepIndex > 0) {
      const secondPlacement = this.upsellPlacementSequence[1];

      if (
        secondPlacement === 'upsell_leadership_summaries' ||
        secondPlacement === 'upsell_leadership_productivity_summaries'
      ) {
        const nextSummariesPlacement: Placement = this.purchasedUpsells.has(
          'upsell_productivity_summaries'
        )
          ? 'upsell_leadership_summaries'
          : 'upsell_leadership_productivity_summaries';

        this.setRegularUpsellPlacementAtIndex(1, nextSummariesPlacement);
      }
    }

    const voicePresetsPlacementIndex = this.upsellPlacementSequence.findIndex(
      placement => placement === 'upsell_voice_presets_discount'
    );

    if (voicePresetsPlacementIndex === -1) {
      return;
    }

    const nextPlacement: Placement =
      this.purchasedUpsells.size > 0
        ? 'upsell_voice_presets' // this one is 2 times more expensive than the discount - in AB 1.5x has won
        : 'upsell_voice_presets_discount';

    this.setRegularUpsellPlacementAtIndex(voicePresetsPlacementIndex, nextPlacement);
  }

  /**
   * The feature upsells' price rides on the placement string (_x1.5), so every comparison
   * against a known addon has to go through the base name first.
   */
  private getPlacementBase(placement: Placement): string {
    return placement.replace(/_x1\.5$/, '');
  }

  private setRegularUpsellPlacementAtIndex(index: number, nextPlacement: Placement): void {
    if (this.upsellPlacementSequence[index] !== nextPlacement) {
      this.upsellPlacementSequence[index] = nextPlacement;
      this.upsellOffers[index] = null;
    }
  }

  private selectAddonOffer(addons: Addon[], placement: Placement): UpsellOffer | null {
    if (!addons || addons.length === 0) {
      return null;
    }

    for (const addon of addons) {
      const offer = this.createUpsellOffer(addon, placement);

      if (offer) {
        return offer;
      }
    }

    return null;
  }

  private createUpsellOffer(addon: Addon, placement: Placement): UpsellOffer | null {
    const slug = this.extractAddonSlug(addon.code);

    if (!slug) {
      console.warn('Unable to determine addon slug from code', addon.code);
      return null;
    }

    const metadata = ADDON_METADATA[slug];

    if (!metadata) {
      console.warn('No UI metadata configured for addon slug', slug);
      return null;
    }

    const { image, ...uiConfig } = metadata;

    return {
      ...uiConfig,
      imageClass: image?.class,
      imageStyle: image && this.buildUpsellImageStyle(image),
      addonCode: addon.code,
      placement,
      priceLabel: formatCurrency(addon.currency, addon.price),
      fullPriceLabel: formatCurrency(addon.currency, addon.compareAtPrice),
      discountLabel: this.getDiscountLabel(addon),
      value: addon.price,
      currency: addon.currency,
    };
  }

  private buildUpsellImageStyle(image: AddonImageConfig): Record<string, string> {
    const lang = (this.locale || '').trim().toLowerCase().split(/[-_]/)[0];
    // Non-localized artwork lives directly under upsells/; localized sets get a locale dir.
    const dir = image.localized ? `${UPSELL_IMAGE_LOCALES.includes(lang) ? lang : 'en'}/` : '';
    const url = (suffix: string) => `url('/assets/upsells/${dir}${image.name}${suffix}.png')`;

    return image.themed
      ? { '--upsell-img': url('_white'), '--upsell-img-dark': url('_dark') }
      : { '--upsell-img': url('') };
  }

  private extractAddonSlug(code: string): string | null {
    if (!code) {
      return null;
    }

    const parts = code.split('.');

    return parts.length >= 3 ? parts[2] : null;
  }

  private getDiscountLabel(addon: Addon): string {
    const { discount, compareAtPrice, price, currency } = addon;

    if (discount) {
      if (discount.units === 'PERCENTAGE') {
        return this.formatPercent(discount.amount);
      }

      return formatCurrency(currency, discount.amount);
    }

    if (!compareAtPrice || compareAtPrice <= price) {
      return this.formatPercent(0);
    }

    const percentDiscount = Math.round((1 - price / compareAtPrice) * 100);

    return this.formatPercent(percentDiscount);
  }

  // Turkish places the percent sign before the number (%60), other locales after (60%)
  private formatPercent(amount: number): string {
    return this.locale === 'tr' ? `%${amount}` : `${amount}%`;
  }

  /**
   * Track purchase event for Axon pixel
   * Parses product_code to extract item name (e.g., '1-week', '4-week', '12-week')
   */
  private trackSubscriptionPurchase(productCode: string | null): void {
    if (!productCode) return;

    // Get value from stored payment data if available
    const storedData = this.localStorageService.get<{ value: number; currency: string }>(
      LocalStorageKey.paymentValue
    );

    this.axonService.purchase({
      item: productCode,
      value: storedData?.value || 10,
      currency: storedData?.currency || 'USD',
      transaction_id: this.getTransactionId(productCode),
    });
    // this.newsbreakService.completePayment({
    //   value: storedData?.value,
    //   currency: storedData?.currency,
    // });
  }

  private getTransactionId(code: string): string {
    const stripeCustomerId = this.userInfo.getStripeCustomerId();

    if (!stripeCustomerId) {
      return `txn_${code}_${Date.now()}`;
    }

    return `txn_${code}_${stripeCustomerId}`;
  }
}
